import EntityModel from '@/common/models/entity-model';
import ContactModel from '@/common/models/contact-model';
import WebsiteModel from '@/common/models/website-model';
import LeadSourceModel from '@/common/models/lead-source-model';
import CustomerModel from '@/common/models/customer-model';
import OrganizationModel from '@/common/models/organization-model';
import InvoiceItemModel from '@/common/models/invoice-item-model';
import InvoiceRetryInstructionModel from '@/common/models/invoice-retry-instruction-model';
import TransactionModel from '@/common/models/transaction-model';
import compare from '@/common/helpers/compare';
import unKebabCase from '@/common/helpers/un-kebab-case';
import Moment from 'moment';

export default class InvoiceModel extends EntityModel {
    static Status = {
        draft: 'draft',
        upcoming: 'upcoming',
        pastDue: 'past-due',
        paid: 'paid',
        unpaid: 'unpaid',
        abandoned: 'abandoned',
        delinquent: 'delinquent',
        voided: 'voided',
        refunded: 'refunded',
        partiallyRefunded: 'partially-refunded',
        partiallyPaid: 'partially-paid',
        disputed: 'disputed',
    };

    static Type = {
        initial: 'initial',
        renewal: 'renewal',
        interim: 'interim',
        cancellation: 'cancellation',
        oneTime: 'one-time',
        refund: 'refund',
        charge: 'charge',
    };

    hydrate({
        abandonedTime = null,
        amount = null,
        amountDue = null,
        autopayRetryNumber = null,
        autopayScheduledTime = null,
        billingAddress = null,
        billingContactId = null,
        collectionPeriod = null,
        createdTime = null,
        currency = null,
        customerId = null,
        delinquentCollectionPeriod = null,
        deliveryAddress = null,
        discounts = null,
        discountAmount = null,
        dueTime = null,
        id = null,
        invoiceNumber = null,
        issuedTime = null,
        items = null,
        transactions = null,
        notes = null,
        organizationId = null,
        paidTime = null,
        poNumber = null,
        retryInstruction = null,
        shippingAmount = null,
        status = InvoiceModel.Status.draft,
        subscriptionId = null,
        subtotalAmount = null,
        taxes = null,
        taxAmount = null,
        updatedTime = null,
        voidedTime = null,
        websiteId = null,
        ...otherFields
    } = {}) {
        this.abandonedTime = abandonedTime;
        this.autopayRetryNumber = autopayRetryNumber;
        this.autopayScheduledTime = autopayScheduledTime;
        this.amount = amount < 0 ? 0 : amount;
        this.billingAddress = new ContactModel(billingAddress || {});
        this.billingContactId = billingContactId;
        this.collectionPeriod = collectionPeriod;
        this.createdTime = createdTime;
        this.currency = currency;
        this.customerId = customerId;
        this.delinquentCollectionPeriod = delinquentCollectionPeriod;
        this.deliveryAddress = new ContactModel(deliveryAddress || {});
        this.discounts = discounts;
        this.discountAmount = discountAmount;
        this.dueTime = dueTime;
        this.id = id;
        this.invoiceNumber = invoiceNumber;
        this.issuedTime = issuedTime;
        this.items = (items || []).map(item => new InvoiceItemModel(item));
        this.transactions = (transactions || [])
            .map(fields => new TransactionModel(fields))
            // Filtering out void transactions, as they appear in the API response incorrectly,
            // as a result from an issue in our backend. Void transactions are child transactions,
            // while "this.transactions" should contain only parent transactions (sale, auth, etc.)
            .filter(transaction => !transaction.isVoidType)
            // Sorting the transactions by processed time from older to newest,
            // this is the most used order, e.q in the invoice details or invoice email.
            // The scheduled time is used if transaction was not processed yet.
            .sort((trX, trY) => compare.dates(
                trX.processedTime || trX.scheduledTime,
                trY.processedTime || trY.scheduledTime,
            ));
        this.notes = notes;
        this.organizationId = organizationId;
        this.paidTime = paidTime;
        this.retryInstruction = new InvoiceRetryInstructionModel(retryInstruction || {});
        this.shippingAmount = shippingAmount;
        this.status = status;
        this.subscriptionId = subscriptionId;
        this.subtotalAmount = subtotalAmount;
        this.taxes = taxes;
        this.taxAmount = taxAmount;
        this.updatedTime = updatedTime;
        this.voidedTime = voidedTime;
        this.websiteId = websiteId;
        this.poNumber = poNumber;
        this.amountDue = amountDue;

        // embedded entities
        this.customer = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'customer',
            model: CustomerModel,
        });
        this.leadSource = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'leadSource',
            model: LeadSourceModel,
        });
        this.website = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'website',
            model: WebsiteModel,
        });
        this.organization = EntityModel.fromEmbedded({
            fields: otherFields,
            entityName: 'organization',
            model: OrganizationModel,
        });
    }

    get products() {
        return this.items.map(item => (item.product || null)).filter(item => item);
    }

    get plans() {
        return this.items.map(item => (item.plan || null)).filter(item => item);
    }

    get hasSubscription() {
        return this.subscriptionId !== null;
    }

    get hasShipping() {
        return this.items.some(item => (item.product ? item.product.requiresShipping : false));
    }

    get isPayable() {
        const allowed = [
            InvoiceModel.Status.unpaid,
            InvoiceModel.Status.pastDue,
            InvoiceModel.Status.partiallyPaid,
        ];

        return allowed.includes(this.status);
    }

    get isVoidable() {
        const allowed = [
            InvoiceModel.Status.unpaid,
            InvoiceModel.Status.pastDue,
            InvoiceModel.Status.draft,
        ];

        return allowed.includes(this.status);
    }

    get isAbandonable() {
        const allowed = [
            InvoiceModel.Status.unpaid,
            InvoiceModel.Status.pastDue,
            InvoiceModel.Status.draft,
        ];

        return allowed.includes(this.status);
    }

    get isReissuable() {
        const allowed = [
            InvoiceModel.Status.unpaid,
            InvoiceModel.Status.pastDue,
        ];

        return allowed.includes(this.status);
    }

    get isIssuable() {
        const allowed = [
            InvoiceModel.Status.draft,
        ];

        return allowed.includes(this.status);
    }

    get isUpcoming() {
        return this.status === InvoiceModel.Status.upcoming;
    }

    get isTangible() {
        return !this.isUpcoming;
    }

    get refundableTransactions() {
        return this.transactions.filter(transaction => transaction.canRefund
            && transaction.affectsCurrentInvoice);
    }

    get productNames() {
        return [...new Set(this.products.map(product => product.name))];
    }

    /**
     * Returns invoice number in conventional format.
     * Avoid the leaking format rule among many components.
     * @returns {string}
     */
    get invoiceNumberLabel() {
        return `${this.invoiceNumber}`.padStart(6, '0');
    }

    /**
     * Returns status in conventional format.
     * Avoid the leaking format rule among many components.
     * @returns {string}
     */
    get statusLabel() {
        return unKebabCase(this.status);
    }

    get isNotAllowedToUpdate() {
        return this.status === InvoiceModel.Status.paid
            || this.status === InvoiceModel.Status.refunded;
    }

    get preferableAutopayTime() {
        if (this.autopayScheduledTime) {
            return this.autopayScheduledTime;
        }

        const now = Moment.utc().add(10, 'minutes');

        if (this.dueTime && Moment.utc(this.dueTime).valueOf() > now.valueOf()) {
            return this.dueTime;
        }

        return now.toISOString();
    }

    getPayload({retryInstruction = false} = {}) {
        const payload = {
            abandonedTime: this.abandonedTime,
            amount: this.amount,
            autopayRetryNumber: this.autopayRetryNumber,
            autopayScheduledTime: this.autopayScheduledTime,
            billingAddress: this.billingAddress.getPayload(),
            billingContactId: this.billingContactId,
            collectionPeriod: this.collectionPeriod,
            createdTime: this.createdTime,
            currency: this.currency,
            customerId: this.customerId,
            delinquentCollectionPeriod: this.delinquentCollectionPeriod,
            deliveryAddress: this.deliveryAddress.getPayload(),
            discounts: this.discounts,
            discountAmount: this.discountAmount,
            dueTime: this.dueTime,
            id: this.id,
            invoiceNumber: this.invoiceNumber,
            issuedTime: this.issuedTime,
            items: this.items.map(v => v.getPayload()),
            notes: this.notes,
            organizationId: this.organizationId,
            paidTime: this.paidTime,
            poNumber: this.poNumber,
            retryInstruction: this.retryInstruction.hasAttempts
                ? this.retryInstruction.getPayload()
                : null,
            shippingAmount: this.shippingAmount,
            status: this.status,
            subscriptionId: this.subscriptionId,
            subtotalAmount: this.subtotalAmount,
            taxAmount: this.taxAmount,
            taxes: this.taxes,
            updatedTime: this.updatedTime,
            voidedTime: this.voidedTime,
            websiteId: this.websiteId,
            amountDue: this.amountDue,
        };
        // for the retry instruction update case
        // send the minimum amount of data required to prevent
        // side-effects
        // TODO: Consider to add PATCH method to API.
        if (retryInstruction) {
            return {
                customerId: payload.customerId,
                websiteId: payload.websiteId,
                currency: payload.currency,
                notes: payload.notes,
                poNumber: payload.poNumber,
                retryInstruction: payload.retryInstruction,
                autopayScheduledTime: payload.autopayScheduledTime,
            };
        }
        return payload;
    }
}
